import ApiService from './api';
import { TokenService } from './token';

/** Error thrown when login / 2FA authentication fails; carries the HTTP status. */
class AuthenticationError extends Error {
  /** HTTP status code (or backend error code) of the failed auth request */
  errorCode: number;

  constructor(errorCode: number, message: string) {
    super(message);
    this.name = this.constructor.name;
    this.message = message;
    this.errorCode = errorCode;
  }
}

const UserService = {
  /**
   * Login the user and store the access token to TokenService.
   *
   * @returns `{token, two_fa}` — token is only persisted when 2FA is not required
   * @throws AuthenticationError
   **/
  login: async function (
    username: string,
    password: string
  ): Promise<{ token: string; two_fa?: boolean } | undefined> {
    const requestData = {
      method: 'post',
      url: '/auth/login',
      data: {
        username,
        password,
        noBots: true,
      },
    };

    try {
      const response = await ApiService.customRequest(requestData);
      if (!response.data.two_fa) {
        TokenService.saveToken(response.data.token);
        ApiService.setHeader();
        ApiService.mount401Interceptor();
      }

      return {
        token: response.data.token,
        two_fa: response.data.two_fa,
      };
    } catch (error: any) {
      if (error.response) {
        let errorMessage = error.response?.data?.detail || error.response?.data?.error;
        // Non-string payloads and rate limits fall back to the generic login error text
        if (typeof errorMessage !== 'string' || error.response?.status === 429) errorMessage = '';
        throw new AuthenticationError(
          error.response.status,
          errorMessage
        );
      }
      throw new AuthenticationError(0, error.message || 'Network error');
    }
  },
  /**
   * Completes a 2FA login.
   * @param twofa_token The one-time 2FA code entered by the user
   * @param usertoken The intermediate user token returned by the first login step
   * @returns The final access token, or `null` when the backend returned none
   * @throws AuthenticationError
   */
  twofaLogin: async function (
    twofa_token: string,
    usertoken: string
  ): Promise<string | null | undefined> {
    const requestData = {
      method: 'post',
      url: '/auth/twofaLogin',
      data: {
        twofa_token,
        usertoken,
        noBots: true,
      },
    };

    try {
      const response = await ApiService.customRequest(requestData);
      if (response.data && response.data.token) {
        TokenService.saveToken(response.data.token);
        ApiService.setHeader();
        ApiService.mount401Interceptor();

        return response.data.token;
      } else {
        return null;
      }
    } catch (error: any) {
      if (error.response) {
        throw new AuthenticationError(
          error.response.status,
          error.response.data.detail
        );
      }
      throw new AuthenticationError(0, error.message || 'Network error');
    }
  },
  async requestPasswordReset(username: string): Promise<any> {
    return await ApiService.post('/auth/forgot', {
      username,
    });
  },

  /**
   * @param token Password-reset token from the "forgot password" mail
   */
  async setNewPassword(
    username: string,
    password: string,
    token: string
  ): Promise<any> {
    const response = await ApiService.post('/auth/changeforgotten', {
      username,
      password,
      token,
    });
    return response;
  },

  /**
   * @param token Invite token from the invitation mail
   */
  async completeInvite(
    username: string,
    password: string,
    token: string
  ): Promise<any> {
    const response = await ApiService.post(
      `/auth/invite/validate/${username}/${token}`,
      {
        username,
        password,
        token,
      }
    );
    return response;
  },

  /**
   * @param email Address the invite is sent to
   * @param botId Bot the invited user gets access to
   * @param features Feature flags granted to the invited user
   */
  async requestInvite(
    email: string,
    botId: string,
    features: string[]
  ): Promise<any> {
    const response = await ApiService.post(
      `/auth/invite/generate/${email}/${botId}`,
      {
        email,
        botId,
        features,
      }
    );
    return response;
  },

  /**
   * Logout the current user by removing the token from storage.
   *
   * Will also remove `Authorization Bearer <token>` header from future requests.
   **/
  logout(): void {
    const token = TokenService.getToken();
    try {
      ApiService.post('/auth/logout', { token }); // async logic
    } catch (error) { /* empty */ }
    /* Because of 400 interceptor in ApiService the response is not returned when logout fails (e.g. on not valid token)
      That's why we can not show detailed error message here
      So we will just proceed with backwards compatibility */

    // Remove the token and remove Authorization header from Api Service as well
    TokenService.removeToken();
    ApiService.removeHeader();
    ApiService.unmount401Interceptor();
  },

  /**
   * Decodes the payload part of a JWT without verifying the signature.
   * @returns The decoded JWT payload object, or `null` when no token is given
   */
  decodeToken(token: string | null | undefined): any {
    if (!token) {
      return null;
    }
    const base64Url = token.split('.')[1];
    return JSON.parse(window.atob(base64Url!));
  },

  /**
   * Checks if user has provided feature or one of features
   * @param user User object (must expose a `features` string array)
   * @param feature feature name to check, or a list where any match counts
   */
  hasFeature(user: any, feature: string | string[]): boolean {
    if (user && user.features && Array.isArray(user.features)) {
      if (Array.isArray(feature)) {
        return user.features.some((f: string) => feature.includes(f));
      }
      return user.features.some((f: string) => f === feature);
    }

    return false;
  },
  /** @returns Axios response with the current user in `data`, or `{data: null}` on error */
  async getUser(): Promise<any> {
    try {
      const response = await ApiService.get(`/auth/me`);
      return response;
    } catch (e) {
      return {
        data: null,
      };
    }
  },

  // @data  {username: [email], language: 'en' | 'de'}
  async saveLanguage(data: {
    /** Email address / login of the user */
    username: string;
    /** UI language to store, 'en' or 'de' */
    language: string;
  }): Promise<any> {
    if (!data?.username || !data?.language) return null;
    try {
      const response = await ApiService.post(
        `/schaltzentrale/bot/language/`,
        data
      );
      return response;
    } catch (error) {
      return {};
    }
  },

  /**
   * @param provider SSO provider key, e.g. 'google'
   * @param data Callback payload (code/nonce etc.) forwarded to the backend
   * @param options `autoLogin`: persist the token immediately when no 2FA is required
   */
  async handleSSOCallback(
    provider: string,
    data: any = {},
    options: { autoLogin?: boolean } = {}
  ): Promise<any> {
    try {
      const response = await ApiService.post(
        `/auth/sso/callback/${provider}`,
        data
      );

      if (options.autoLogin && !response.data.two_fa) {
        TokenService.saveToken(response.data.token);
        ApiService.setHeader();
        ApiService.mount401Interceptor();
      }

      return response.data;
    } catch (e: any) {
      throw new Error(e.response?.data?.error || e.message);
    }
  },

  /**
   * Update a bot user's active state and feature set.
   *
   * Uses the schaltzentrale user endpoint which validates the submitted feature
   * set on the backend and returns per-item error results when invalid, so the
   * caller can surface them instead of silently persisting a bad state.
   *
   * @returns `{ success: true, data }` on success, `{ success: false, error }` otherwise.
   */
  async updateUser({
    active,
    features,
    username,
  }: {
    active: boolean;
    features: string[];
    username: string;
  }): Promise<{ success: boolean; data?: any; error?: any }> {
    try {
      const response = await ApiService.put(`/schaltzentrale/user`, {
        active,
        feature: features,
        username,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error };
    }
  },
};

export default UserService;

export { UserService, AuthenticationError };
